import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { countries } from "country-flags-svg";
import ReactFlagsSelect from "react-flags-select";
import { useSelector } from "react-redux";
import { commonUtilitySelector } from "../../redux/commonUtilitySlice";

const LANGUAGE_CONFIG = [
  { code: "US", label: "English" },
  { code: "IN", label: "Hindi" },
  { code: "SA", label: "Arabic" },
  { code: "RU", label: "Russian" },
];

const flagByCode = Object.fromEntries(
  LANGUAGE_CONFIG.map(({ code }) => [
    code,
    countries.find((c) => c.iso2 === code)?.flag,
  ])
);

const LanguageCaret = () => (
  <span className="language-selector-caret" aria-hidden="true">
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const isCompactViewport = () =>
  typeof window !== "undefined" && window.innerWidth <= 1024;

const NavbarLanguageSelector = ({ selectedFlag, onSelect }) => {
  const { isMobile, isTablet } = useSelector(commonUtilitySelector);
  const isCompact = isMobile || isTablet || isCompactViewport();

  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 160 });

  const updateMenuPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuWidth = 160;
    const left = Math.min(
      Math.max(8, rect.right - menuWidth),
      window.innerWidth - menuWidth - 8
    );
    setMenuPos({
      top: rect.bottom + 6,
      left,
      width: menuWidth,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isCompact || !menuOpen) return undefined;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isCompact, menuOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isCompact || !menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isCompact, menuOpen]);

  useEffect(() => {
    if (!isCompact || !menuOpen) return undefined;
    const onDown = (e) => {
      if (
        wrapRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      ) {
        return;
      }
      setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [isCompact, menuOpen]);

  const pickLanguage = (code) => {
    setMenuOpen(false);
    onSelect(code);
  };

  if (!isCompact) {
    return (
      <>
        <ReactFlagsSelect
          countries={["US", "IN", "SA", "RU"]}
          customLabels={{
            US: "English",
            IN: "Hindi",
            SA: "Arabic",
            RU: "Russian",
          }}
          selected={selectedFlag}
          onSelect={onSelect}
          showOptionLabel={true}
          showSelectedLabel={true}
          selectedSize={14}
          optionsSize={14}
          placeholder="Select Language"
        />
        <LanguageCaret />
      </>
    );
  }

  return (
    <div className="navbar-language-compact" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className="navbar-language-trigger"
        aria-haspopup="listbox"
        aria-expanded={menuOpen}
        aria-label="Select language"
        onClick={() => setMenuOpen((open) => !open)}
      >
        {flagByCode[selectedFlag] ? (
          <img
            className="navbar-language-trigger__flag"
            src={flagByCode[selectedFlag]}
            alt=""
            width={20}
            height={14}
            loading="lazy"
          />
        ) : null}
      </button>
      <LanguageCaret />
      {menuOpen
        ? createPortal(
            <ul
              ref={menuRef}
              className="navbar-language-menu"
              role="listbox"
              style={{
                position: "fixed",
                top: menuPos.top,
                left: menuPos.left,
                width: menuPos.width,
                zIndex: 2147483646,
              }}
            >
              {LANGUAGE_CONFIG.map(({ code, label }) => (
                <li key={code} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={selectedFlag === code}
                    className={`navbar-language-menu__option${
                      selectedFlag === code
                        ? " navbar-language-menu__option--current"
                        : ""
                    }`}
                    onClick={() => pickLanguage(code)}
                  >
                    {flagByCode[code] ? (
                      <img
                        className="navbar-language-menu__flag"
                        src={flagByCode[code]}
                        alt=""
                        width={20}
                        height={14}
                        loading="lazy"
                      />
                    ) : null}
                    <span className="navbar-language-menu__label">{label}</span>
                  </button>
                </li>
              ))}
            </ul>,
            document.body
          )
        : null}
    </div>
  );
};

export default NavbarLanguageSelector;
